/// <reference path="../../typings/browser.d.ts" />

namespace Application.Controllers {
  "use strict";
  import IConnectedBlock = Models.IConnectedBlock;
  import IShowModalParameters = ngModal.IShowModalParameters;
  import ImportedBundle = Models.ImportedBundle;
  import BundleRow = Models.BundleRow;
  import Utils = Common.Utils;
  import TableBundle = Models.TableBundle;
  import Bundle = Models.Bundle;
  import BundleBlock = Models.BundleBlock;
  import BundleProbe = Models.BundleProbe;
  import ConnRoomTakeResult = Application.Models.ConnRoomTakeResult;

  /**
   * to use csv import
   */
  declare const $: any;

  export class AdminBundlesController {
    public static $name: string = "adminBundlesController";
    public static $inject: string[] = [
      "connectionRoomService",
      "ModalService",
      "inventoryBundleService",
      "tsProbeService",
      "blockService",
      "$q",
      "presetPathService",
      "blockConnectionBlocksService",
      "backendService",
      "streamPipeService",
      "flexCollectorService"
    ];

    private bundleNamePlaceholder = "{bundleName}";
    private importInfoTitle = "Import bundle:";
    private importInfoMessage =
      'Bundle "' + this.bundleNamePlaceholder + '" created successfully!';
    private importErrorTitle = "Import error:";
    private importErrorMessage =
      'A bundle with name: "' +
      this.bundleNamePlaceholder +
      '" already exists. Please change bundle name!';
    private emptyBundleErrorMessage = "Impossible to create an empty bundle!";
    private missingSourcesErrorMessage = "Missing SOURCE at lines: ";
    private missingDestinationsErrorMessage = "Missing DESTINATION at lines: ";
    private deleteInfoTitle = "Delete bundle:";
    private deleteConfirmMessage =
      'Do you want to delete the "' + this.bundleNamePlaceholder + '" bundle?';
    private deleteInfoMessage =
      'Bundle "' + this.bundleNamePlaceholder + '" deleted successfully!';
    private deleteErrorTitle = "Delete error:";
    private takeAllTitle = "Multiple take operations:";
    private takeAllMessage =
      "All takes are going to be validated and performed, do you want to continue?";
    private invalidConnectionsMessage = "There are some not valid connections!";
    private takeAllErrorTitle = "Take error: ";
    private dropAllTitle = "Multiple drop operations:";
    private dropAllMessage =
      "Do you want to drop all the connections for the displayed sinks?";
    private dropAllErrorTitle = "Drop error: ";
    private lockedBlocksErrorMessage =
      "Some of the blocks are locked, please unlock them before doing a drop!";
    private probeAllTitle = "Multiple probe operations:";
    private probeAllMessage = "Do you want to probe all the displayed sinks?";
    private stopProbingAlMessage =
      "Do you want to stop probing all the displayed sinks?";
    private unknownErrorTitle = "Error:";
    private unknownErrorMessage = "Something went wrong, please try again";
    private unexistingBundleMessage =
      'Bundle "' + this.bundleNamePlaceholder + '" does not exist!';
    private lockedBlockException =
      "io.epiclabs.flex.flexo.instances.LockedBlockException";
    private selectABundleMessage = "Please select a bundle first";

    private showSpinner: boolean;
    private buttonsDisabled: boolean;
    private selectedBundle: string;
    private selectedBundleIndex: number;
    private selectedTableBundle: TableBundle;
    private bundles: string[];
    private tableBundles: TableBundle[];
    private selectedPipes: any[];
    private containerCheckInterval: any = [];

    constructor(
      private connectionRoomService: Services.ConnectionRoomService,
      private modalService: ngModal.IModalService,
      private inventoryBundleService: Services.InventoryBundleService,
      private tsProbeService: Services.TsProbeService,
      private blockService: Services.BlockService,
      private $q: ng.IQService,
      private presetPathService: Services.PresetPathService,
      private blockConnectionBlockService: Services.BlockConnectionBlocksService,
      private backendService: Services.BackendService,
      private streamPipeService: Services.StreamPipeService,
      private flexCollectorService: Services.FlexCollectorService
    ) {
      this.buttonsDisabled = true;
      this.showSpinner = true;
      this.getAllBundles();
      this.setIntervalForConnectionsStatus();
      this.showSpinner = false;
      this.buttonsDisabled = false;
    }

    /**
     * Retrieves all the bundles from database and sets the firs one as selected.
     */
    private getAllBundles() {
      this.tableBundles = [];
      this.bundles = [];
      const result = this.inventoryBundleService.getAllBundles();
      result
        .then((data) => {
          data.forEach((bundle) => this.addNewBundle(bundle));
        })
        .catch((err) =>
          this.showErrorModal(this.unknownErrorTitle, this.unknownErrorMessage)
        );
    }

    /**
     * Sets an interval of 6 seconds to call the backend to get probe and connection status of the bundle.
     */
    private setIntervalForConnectionsStatus(): void {
      setInterval(() => {
        this.setConnectionsInfo();
        this.setProbesInfo();
      }, 20000);
    }

    /**
     * Adds a new bundle in the lists
     * @param newBundle
     */
    private addNewBundle(newBundle: Bundle): void {
      this.tableBundles.push(new TableBundle(newBundle));
      this.bundles.push(newBundle.name);
    }

    /**
     * Sets the first bundle of thie list as selected, if exists
     */
    private selectFirstBundle(): void {
      if (this.areThereBundles()) {
        this.changeSelectedBundle(this.tableBundles[0].name);
      } else {
        this.selectedBundle = undefined;
        this.selectedBundleIndex = undefined;
        this.selectedTableBundle = undefined;
      }
    }

    /**
     * Changes the selected bundle in the page
     * @param newBundle
     */
    private changeSelectedBundle(newBundle: string): void {
      this.selectedBundleIndex = this.bundles.indexOf(newBundle);
      this.selectedBundle = this.tableBundles[this.selectedBundleIndex].name;
      this.selectedTableBundle = this.tableBundles[this.selectedBundleIndex];
      this.setConnectionsInfo();
      this.setProbesInfo();
      this.checkLockedBlocks();
      this.getPipeUrl();
    }

    /**
     * Adds connections info to the selected bundle.
     */
    private async setConnectionsInfo() {
      if (this.selectedTableBundle) {
        this.selectedTableBundle.tableRows.forEach(async (item) => {
          if (item.destination) {
            const connectedBlocksList: IConnectedBlock[] =
              await this.connectionRoomService.getMatrixRoomBlockConnections(
                item.destination.id
              );
            item.destionationConnection =
              connectedBlocksList.length > 0
                ? new BundleBlock(
                    connectedBlocksList[0].id,
                    connectedBlocksList[0].name
                  )
                : null;
            item.isConnectedToThisSource =
              item.destionationConnection &&
              item.destionationConnection.id === item.source.id;
            item.isConnectedToOtherSource =
              item.destionationConnection &&
              item.destionationConnection.id !== item.source.id;
          }
        });
      }
    }

    /**
     * Adds probes info to the selected bundle.
     */
    private async setProbesInfo() {
      if (this.selectedTableBundle) {
        let allProbes = await this.tsProbeService.getAllProbedBlocks();
        this.selectedTableBundle.tableRows.forEach((item) => {
          let sourceProbe = allProbes.allProbedBlocks[item.source.id];
          let destinationProbe = item.destination
            ? allProbes.allProbedBlocks[item.destination.id]
            : undefined;
          item.actualSourceProbe = sourceProbe
            ? new BundleProbe(
                sourceProbe.defaultTsProbe.tsProbe.networkNodeId,
                sourceProbe.defaultTsProbe.tsProbe.networkNodeName
              )
            : undefined;
          item.actualDestinationProbe = destinationProbe
            ? new BundleProbe(
                destinationProbe.defaultTsProbe.tsProbe.networkNodeId,
                destinationProbe.defaultTsProbe.tsProbe.networkNodeName
              )
            : undefined;
        });
      }
    }

    /**
     * Imports a new csv bundle and saves it into the database
     */
    public importCsv(): void {
      let self: AdminBundlesController = this;
      const inputTagId = "fileImport";
      let fileinput: HTMLInputElement = <HTMLInputElement>(
        document.getElementById(inputTagId)
      );
      $("#fileImport").click();
      fileinput.addEventListener("change", function () {
        let f: File = (<HTMLInputElement>fileinput).files[0];
        const newBundleName = Utils.getFileBaseName(f.name);
        if (Utils.isInArray(newBundleName, self.bundles)) {
          self.showErrorModal(
            self.importErrorTitle,
            self.importErrorMessage.replace(
              self.bundleNamePlaceholder,
              newBundleName
            )
          );
        } else {
          self.parseCsv("#" + inputTagId, newBundleName);
        }
        fileinput.value = ""; // this is required to upload a file with the same name two consecutive times
      });
    }

    /**
     * It performs the csv parsing, validates the content and sends data to the backend to save it.
     * @param bundleName
     */
    private parseCsv(inputTagId: string, bundleName: string) {
      $(inputTagId).parse({
        config: {
          delimiter: "auto",
          skipEmptyLines: true,
          complete: (result: any) => {
            this.buttonsDisabled = true;
            this.showSpinner = true;
            const bundle = new ImportedBundle(bundleName, result.data);
            const errors = this.validateBundle(bundle.bundleRows);
            if (errors.length > 0) {
              this.showErrorModal(this.importErrorTitle, errors.join(".<br>"));
            } else {
              if (!this.checkBundleLength(bundle.bundleRows)) {
                this.buttonsDisabled = false;
                this.showSpinner = false;
                return;
              }
              const response = this.inventoryBundleService.createBundle(bundle);
              response
                .then((data) => {
                  this.showInfoModal(
                    this.importInfoTitle,
                    this.importInfoMessage.replace(
                      this.bundleNamePlaceholder,
                      bundleName
                    )
                  );
                  this.addNewBundle(data);
                  this.changeSelectedBundle(bundleName);
                  // after selectedBundle is selected, we handle custom parameters
                })
                .catch((errorResponse) => {
                  const errorMessage =
                    errorResponse.data.status !== 500
                      ? errorResponse.data.message
                      : this.unknownErrorMessage;
                  this.showErrorModal(this.importErrorTitle, errorMessage);
                });
            }
            this.showSpinner = false;
            this.buttonsDisabled = false;
          },
        },
      });
    }
    /**
     *
     */
    checkBundleLength(bundleRows: any) {
      if (bundleRows.length > 500) {
        this.showErrorModal(
          this.importErrorTitle,
          "Bundle is too big (500 max)"
        );
        return false;
      }
      return true;
    }

    /**
     * Returns an array of errors, the array will be empty if the bundle is valid.
     * A Bundle is invalid if a row is missing a source and/or both destination and decoder are missing.
     * @param bundle Bundle to validate
     * @returns an array of errors.
     */
    private validateBundle(bundleRows: BundleRow[]): string[] {
      const excelOffset = 1;
      let errors: string[] = [];
      let missingSourceIndexes: number[] = [];
      let missingDestinationIndexes: number[] = [];
      let invalidPassphrase: boolean = false;

      bundleRows.forEach((bundleRow, index) => {
        if (!bundleRow.source) {
          missingSourceIndexes.push(index + excelOffset);
        }
        if (!bundleRow.destination) {
          missingDestinationIndexes.push(index + excelOffset);
        }

        console.log("bundleRow ---> ", bundleRow);
        let parsedPassphrase = JSON.parse(bundleRow.parameters).passphrase;
        let invalidChar: boolean = parsedPassphrase.indexOf('=') !== -1 || parsedPassphrase.indexOf('&') !== -1 || parsedPassphrase.indexOf('?') !== -1;
        invalidPassphrase = invalidChar || invalidPassphrase;
      });
      if(invalidPassphrase) errors.push("Invalid passphrase, it can't contain '=', '&' or '?' characters.");
      if (bundleRows.length === 0) {
        errors.push(this.emptyBundleErrorMessage);
      }
      if (missingSourceIndexes.length > 0) {
        errors.push(this.missingSourcesErrorMessage + missingSourceIndexes);
      }
      if (missingDestinationIndexes.length > 0) {
        errors.push(
          this.missingDestinationsErrorMessage + missingDestinationIndexes
        );
      }
      return errors;
    }

    /**
     * Returns true if there is at least one bundle.
     * @returns true if there is at least one bundle.
     */
    private areThereBundles(): boolean {
      return this.tableBundles && this.tableBundles.length > 0;
    }

    /**
     * Opens a modal to delete the selected bundle.
     */
    private deleteBundle() {
      if (!this.checkIfAnyBundleIsSelected()) return;
      this.showConfirmModal(
        this.deleteInfoTitle,
        this.deleteConfirmMessage.replace(
          this.bundleNamePlaceholder,
          this.selectedBundle
        ),
        () => {
          this.buttonsDisabled = true;
          this.showSpinner = true;
          const deletePromise = this.inventoryBundleService.deleteBundle(
            this.selectedTableBundle.id
          );
          deletePromise.then((status) => {
            if (status === 200) {
              this.showInfoModal(
                this.deleteInfoTitle,
                this.deleteInfoMessage.replace(
                  this.bundleNamePlaceholder,
                  this.selectedBundle
                )
              );
              this.removeBundle();
              this.selectFirstBundle();
            } else {
              this.showErrorModal(
                this.deleteErrorTitle,
                this.unknownErrorMessage
              );
            }
          });
          this.showSpinner = false;
          this.buttonsDisabled = false;
        }
      );
    }

    checkIfAnyBundleIsSelected() {
      if (this.selectedBundle) {
        return true;
      } else {
        this.showErrorModal(this.unknownErrorTitle, this.selectABundleMessage);
        return false;
      }
    }

    /**
     * Generate a CSV file with the Bundle data.
     */
    private downloadBundle() {
      if (!this.checkIfAnyBundleIsSelected()) return;
      const bundleRows: BundleRow[] = [];

      this.selectedTableBundle.tableRows.forEach((tableRow) =>
        bundleRows.push(...tableRow.convertToBundleRows())
      );

      const csvRows: string[] = bundleRows.map((bundleRow) => {
        const parameters = bundleRow.parameters
          ? this.mapParametersToDownload(bundleRow.parameters)
          : null;
        let row = `${bundleRow.source};${bundleRow.destination}`;
        row = parameters ? `${row};${parameters}` : row;
        return row;
      });

      Utils.generateCsvDownload(csvRows, this.selectedBundle, true);
    }

    /**
     * Maps parameters when downloading bundle
     */
    private mapParametersToDownload(parameters: any) {
      let params = [];
      if (parameters.passphrase) params.push(parameters.passphrase);
      else params.push("");
      if (parameters.takeLocked) params.push(parameters.takeLocked);
      else params.push("");
      if (parameters.takeLockedComment)
        params.push(parameters.takeLockedComment);
      else params.push("");
      return params.join(";");
    }

    /**
     * Removes a bundle from the lists
     */
    private removeBundle(): void {
      this.tableBundles.splice(this.selectedBundleIndex, 1);
      this.bundles.splice(this.selectedBundleIndex, 1);
    }

    /**
     * Shows the confirm modal to perform a take for every row in the bundle
     */
    private takeAll() {
      if (!this.checkIfAnyBundleIsSelected()) return;
      this.showConfirmModal(
        this.takeAllTitle,
        this.takeAllMessage,
        async () => {
          this.buttonsDisabled = true;
          this.showSpinner = true;
          if (
            (await this.validateBundleConnections()) &&
            (await this.dropBundle())
          ) {
            await this.takeBundle();
            this.showInfoModal("Done", "Success");
          }
          this.showSpinner = false;
          this.buttonsDisabled = false;
          setTimeout(() => {
            this.getPipeUrl();
            this.checkLockedBlocks();
          }, 5000);
        }
      );
    }

    /**
     * Returns true if the bundle connection validation is performed without errors
     * @returns true if the bundle connection validation is performed without errors
     */
    private async validateBundleConnections(): Promise<boolean> {
      const validationStatus: any =
        await this.inventoryBundleService.validateBundleConnections(
          this.selectedTableBundle.id
        );
      this.handleBulkOpsErrors(validationStatus);
      return validationStatus.status === 200;
    }

    /**
     * Returns true if the drop bundle operation is performed without errors.
     * This method must be called only before a take all operation.
     * @returns true if the drop bundle operation is performed without errors
     */
    private async dropBundle(): Promise<boolean> {
      const dropStatus =
        await this.inventoryBundleService.dropBundleConnections(
          this.selectedTableBundle.id
        );
      this.handleBulkOpsErrors(dropStatus);
      return dropStatus === 200;
    }

    /**
     * Returns true if the drop bundle operation is performed without errors.
     * This method must be called only before a take all operation.
     * @returns true if the drop bundle operation is performed without errors
     */
    private async takeBundle() {
      // we set the passphrase before doing the bundle take
      await this.handleCustomParams(true);
      const takeResult =
        await this.inventoryBundleService.takeBundleConnections(
          this.selectedTableBundle.id
        );
      if (takeResult.status === 200) {
        // we lock connection and update the lockedComment
        await this.handleCustomParams(false, true);
        const takeData: ConnRoomTakeResult[] = takeResult.data;
        if (takeData.some((elem) => !elem.ok)) {
          this.showErrorModal(
            this.takeAllErrorTitle,
            this.invalidConnectionsMessage
          );
        }
      } else {
        this.handleBulkOpsErrors(takeResult.status);
      }
    }

    private async unlockConnections() {
      if (!this.checkIfAnyBundleIsSelected()) return;
      let selectedBundle: TableBundle = this.tableBundles.filter(
        (bundle: TableBundle) => {
          return bundle.name === this.selectedBundle;
        }
      )[0];

      for (const row of selectedBundle.tableRows) {
        await this.handleBlockParameters(row, true);
      }
      this.showInfoModal("Done", "Success");

      setTimeout(() => {
        this.checkLockedBlocks();
      }, 5000);
    }

    private handleCustomParams(
      passphraseOnly: boolean = false,
      blockParametersOnly: boolean = false
    ) {
      return new Promise<void>(async (resolve) => {
        let selectedBundle: TableBundle = this.tableBundles.filter(
          (bundle: TableBundle) => {
            return bundle.name === this.selectedBundle;
          }
        )[0];

        for (const row of selectedBundle.tableRows) {
          if (!row.parameters) continue;
          if (row.parameters.passphrase && passphraseOnly) {
            await this.handlePassphrase(row);
          }
          if (
            (row.parameters.takeLocked ||
              row.parameters.takeLocked ||
              row.parameters.rtpStrip) &&
            blockParametersOnly
          ) {
            await this.handleBlockParameters(row, false);
          }
        }
        resolve();
      });
    }

    private async handlePassphrase(row: any) {
      let presetPath =
        await this.presetPathService.getStickyConnectionsByBlockId(
          row.destination.id
        );
      let presetPathBlockConnection = presetPath[0].origBlockConnection;

      let updateBlockConnection: Models.IUpdateBlockConnection = {
        id: presetPathBlockConnection.id,
        targetPorts: presetPathBlockConnection.targetPorts,
        parameters: { passphrase: row.parameters.passphrase },
      };

      await this.blockConnectionBlockService
        .updateBlockConnection(updateBlockConnection)
        .catch((res) => {
          console.log("Error updating passphrase: ", res);
        });
    }

    private async handleBlockParameters(row: any, unlock: boolean) {
      const destinationBlock = await this.blockService.findById(
        row.destination.id
      );
      // fixing weird url
      const blockUrl: string = destinationBlock.href.split("{")[0];

      return this.backendService
        .put(blockUrl, {
          name: destinationBlock.name,
          config: destinationBlock.config,
          locked:
            unlock == true ? "false" : row.parameters.takeLocked.toLowerCase(),
          lockedComment:
            row.parameters && row.parameters.takeLockedComment
              ? row.parameters.takeLockedComment
              : destinationBlock.lockedComment,
        })
        .then(
          (
            result: ng.IHttpPromiseCallbackArg<
              Models.IHalElement<Models.INetworkNode>
            >
          ) => {
            return result.data;
          }
        )
        .catch(() => {
          console.log("there was a problem updating custom parameters");
        });
    }

    private async getPipeUrl() {
      let blockList: any[] = [];
      this.selectedTableBundle.tableRows.forEach(async (row) => {
        blockList.push(row.destination.id);
      });
      const pipes = await this.streamPipeService.getStreamPipesByBlockList(
        blockList
      );

      this.selectedTableBundle.tableRows.forEach(async (row, index) => {
        //if pipe connection is done, retrieve pipe id from href and add it to row
        if (pipes[index].length > 0){
          row.pipe = pipes[index][0].href.split("/").pop();

          const response = await this.streamPipeService.getStreamPipeViewByStreamId(parseInt(row.pipe));
          // const blockId = response[1].blockId; 

          for (const block of response) {
            if(block.blockI.blockTypeNaturalKey.toLowerCase().indexOf("srt") > -1){
              this.containerHealthInterval(block.blockId, row);
              break;
            }
          }
        }
        else {
          row.srtContainerAlert = false;
          row.pipe = null; }
      }); 
    }

    /**
     * Will check the health of the container twice, once every 30 seconds,
     * if status is not running, will set the alert to true
     */
    private containerHealthInterval(blockId: number, row: any){
      let count = 0;
      const limit = 1;
      let intervalTime = 30000;
      this.containerCheckInterval[blockId] = setInterval(async() => {
        if (count >= limit) {
          clearInterval(this.containerCheckInterval[blockId]);
        }

        const blockInfoRes = await this.flexCollectorService.getDeviceParamsBySimplifiedHref('blocks/' + blockId);
        let blockInfo: Controllers.IDigestedInfo = {};
        for (let p of blockInfoRes.params) {
          blockInfo[p.name] = p.lastValue;
        }
            
        row.srtContainerAlert = blockInfo["out_STATUS"] && (blockInfo["out_STATUS"] as string).toLowerCase().indexOf("running") > -1 ? false : true;
        console.log(`srtContainerAlert----> ${row.srtContainerAlert}, blockInfo["out_STATUS"] ----> ${blockInfo["out_STATUS"]}`);

        count++;
      }, intervalTime);
    }

    private async checkLockedBlocks() {
      let selectedBundle: TableBundle = this.tableBundles.filter(
        (bundle: TableBundle) => {
          return bundle.name === this.selectedBundle;
        }
      )[0];

      for (const row of selectedBundle.tableRows) {
        const destinationBlock = await this.blockService.findById(
          row.destination.id
        );
        if (destinationBlock.locked == true) {
          row.realTakeLocked = true;
        } else {
          row.realTakeLocked = false;
        }
      }
    }

    /**
     * Shows the confirm modal to perform a take for every row in the bundle
     */
    private dropAll() {
      if (!this.checkIfAnyBundleIsSelected()) return;
      this.showConfirmModal(
        this.dropAllTitle,
        this.dropAllMessage,
        async () => {
          this.buttonsDisabled = true;
          this.showSpinner = true;
          const probeStatus = await this.inventoryBundleService.stopProbes(
            this.selectedTableBundle.id
          );
          const stopProbingError = this.handleBulkOpsErrors(probeStatus);
          if (!stopProbingError) {
            const dropResult =
              await this.inventoryBundleService.dropAllBundleConnections(
                this.selectedTableBundle.id
              );
            if (
              dropResult.status === 500 &&
              dropResult.data.exception === this.lockedBlockException
            ) {
              this.showErrorModal(
                this.dropAllErrorTitle,
                this.lockedBlocksErrorMessage
              );
            } else {
              if (this.handleBulkOpsErrors(dropResult.status))
                this.showInfoModal("Done", "Success");
            }
          }
          this.showSpinner = false;
          this.buttonsDisabled = false;
          setTimeout(() => this.getPipeUrl(), 5000);
        }
      );
    }

    /**
     * Probes every block with probe in the bundle.
     */
    private async probeBundle() {
      if (!this.checkIfAnyBundleIsSelected()) return;
      this.showConfirmModal(
        this.probeAllTitle,
        this.probeAllMessage,
        async () => {
          this.buttonsDisabled = true;
          this.showSpinner = true;
          const probeStatus = await this.inventoryBundleService.probeBundle(
            this.selectedTableBundle.id
          );
          this.handleBulkOpsErrors(probeStatus);
          this.showSpinner = false;
          this.buttonsDisabled = false;
        }
      );
    }

    /**
     * Stops the probing for every block with probe in the bundle.
     */
    private async stopProbing() {
      if (!this.checkIfAnyBundleIsSelected()) return;
      this.showConfirmModal(
        this.probeAllTitle,
        this.stopProbingAlMessage,
        async () => {
          this.buttonsDisabled = true;
          this.showSpinner = true;
          const probeStatus = await this.inventoryBundleService.stopProbes(
            this.selectedTableBundle.id
          );
          this.handleBulkOpsErrors(probeStatus);
          this.showSpinner = false;
          this.buttonsDisabled = false;
        }
      );
    }

    /**
     * if the HTTP status is 500 or 404 shows an error message.
     * @param status HTTP status
     * @returns true if there is error is thrown;
     */
    private handleBulkOpsErrors(status: any): boolean {
      let errorMessage = status.status === 500 ? status.data.message : null;
      errorMessage =
        status === 404
          ? this.unexistingBundleMessage.replace(
              this.bundleNamePlaceholder,
              this.selectedBundle
            )
          : errorMessage;
      if (errorMessage !== null) {
        this.showErrorModal(this.takeAllErrorTitle, errorMessage);
      }
      return errorMessage !== null;
    }

    /**
     * Shows an info modal
     * @param title modal title
     * @param description modal description
     */
    private showInfoModal(title: string, description: string): void {
      const modalParameters = {
        templateUrl: Controllers.InfoModalController.templateURL,
        controller: Controllers.InfoModalController.$name,
        inputs: { title: title, description: description },
      };
      this.modalService
        .showModal(modalParameters)
        .then((modal: ngModal.IModalObject) => {
          let modalElem: UIkit.ModalElement = UIkit.modal(
            Controllers.InfoModalController.ID,
            { bgclose: false, modal: false, keyboard: false }
          );
          modalElem.show();
          modal.close.then((result: Object) => modalElem.hide());
        });
    }

    /**
     * Opens a modal with confirmation button
     * @param title modal title
     * @param description modal description
     * @param onConfirmCallback modal callback on confirm
     */
    private showConfirmModal(
      title: string,
      description: string,
      onConfirmCallback: () => any
    ): void {
      const modalParameters = {
        templateUrl: Controllers.ConfirmModalController.templateURL,
        controller: Controllers.ConfirmModalController.$name,
        inputs: {
          title: title,
          description: description,
          name: undefined as string,
          type: "information",
        },
      };
      this.modalService
        .showModal(modalParameters)
        .then((modal: ngModal.IModalObject) => {
          let modalElem: UIkit.ModalElement = UIkit.modal(
            Controllers.ConfirmModalController.ID,
            { bgclose: false, modal: false, keyboard: false }
          );
          modalElem.show();
          modal.close.then((result: Object) => {
            modalElem.hide();
            if (result) {
              onConfirmCallback();
            }
          });
        });
    }

    /**
     * Shows an error modal
     * @param title modal title
     * @param description error description
     */
    private showErrorModal(title: string, description: string) {
      const modalParameteres: IShowModalParameters = {
        templateUrl: Controllers.ErrorModalController.templateURL,
        controller: Controllers.ErrorModalController.$name,
        inputs: { title: title, description: description },
      };
      this.modalService
        .showModal(modalParameteres)
        .then((modal: ngModal.IModalObject) => {
          let modalElem: UIkit.ModalElement = UIkit.modal(
            Controllers.ErrorModalController.ID,
            { bgclose: false, modal: false, keyboard: false }
          );
          modalElem.show();
          modal.close.then((result: Object) => modalElem.hide());
        });
    }
  }
}
